<template>
  <div id="academic_overview">
    <usc-hero :title="this.hero.title" :loResSrc="this.hero.loResSrc" :hiResSrc="this.hero.hiResSrc" :color="this.page.color"></usc-hero>
    <usc-intro :copy="this.intro">
        <p>Today’s business leaders must possess a global mindset that allows them to cross borders effortlessly, whether those boundaries are international, cultural or disciplinary. The USC Marshall undergraduate curriculum is tailored toward that objective.</p>
    </usc-intro>
    <div class="body mb gutter-lg">
      <p><i style="">Every student has distinctive interests and career goals. Marshall gives you the freedom to create an education experience that is unique to your needs and that builds your global and interdisciplinary perspective while maintaining a rigorous core business program. You can take a mix of business and non-business subjects every semester.</i></p>
      <p>The Marshall curriculum blends skills and knowledge in a primary area of expertise with focused experiences outside that arena through minors, double majors, joint degrees, co-curricular activities and experiential learning opportunities. You can customize your academic program, pursue your passions and build the skills to succeed in the classroom and in your dream role in the business world.</p>
      <p>As a comprehensive business school, we also offer nine one-year specialized master programs that let you earn an advanced degree along with your undergraduate degree.</p>
      <p>As you explore the endless combinations available to you at Marshall, we’ll be here to guide you through the process. Our academic advisors can help you design the curriculum path and variety of elective-course combinations best suited to your career goals.</p>
    </div>
    <div class="body mb gutter-lg">
      <img src="../../../static/academics-curriculum-structure-24.png" alt="36 USC Required Courses, 48 Marshall Core, 12 Mrshall Upper-Division Election, 32 Free Electives/Minors"/>
    </div>
    <usc-calloutLink :url="this.calloutLink.url" :urltext="this.calloutLink.urlText"></usc-calloutLink>
    <div class="body mb gutter-lg">
      <h2>Marshall Degrees</h2>
      <p>Each of our dynamic degree programs offers you outstanding academic preparation, rewarding internship and international experiences, unique hands-on learning opportunities, career-building tools and resources, meaningful mentorships and membership in our unparalleled Trojan Family global alumni network. And no matter which degree program you choose, you’ll be able to customize your education to fit your personal goals and interests.</p>
      <div class="gridbox mb">
        <div class="gridbox-items">
          <div class="gridbox-items--img"><img src="../../../static/Icon/23-business-admin.png" alt="Business Administration Icon"/></div>
        </div>
        <div class="gridbox-items">
          <div class="gridbox-items--img"><img src="../../../static/Icon/23-accounting.png" alt="Accounting Icon"/></div>
        </div>
        <div class="gridbox-items">
          <div class="gridbox-items--img"><img src="../../../static/Icon/23-WBB.png" alt="World Bachelor In Business Icon"/></div>
        </div>
      </div>
      <h3><strong>BUSINESS ADMINISTRATION <br />Breadth with depth</strong></h3>
      <p>Our flagship Business Administration program gives you thorough preparation in the cornerstones of business, with a focus on leadership development and the acquisition of managerial skills. The core curriculum emphasizes experiential learning that nurtures entrepreneurial talent and fosters critical thinking while ensuring plentiful elective options.</p>

      <h3><strong>ACCOUNTING <br />Innovative, immersive, international</strong></h3>
      <p>Graduate from the program as both a qualified accountant and well-rounded professional. The Leventhal School of Accounting within the Marshall School of Business offers a rigorous curriculum designed to ensure your technical proficiency, research and analytical skills, ethical decision-making mindset and leadership development. You’ll learn from top faculty and mentors as you build skills for your successful career.</p>

      <h3><strong>WORLD BACHELOR IN BUSINESS <br />Fluency in international commerce</strong></h3>
      <p>The only undergraduate program of its kind, the World Bachelor in Business offers you degrees from three leading research universities and access to three global alumni networks. You’ll benefit from cultural immersion and real-world experiences in Los Angeles, Hong Kong and Milan, and become well-versed in how to conduct business in Asia, Europe and the Americas.</p>
    </div>
    <usc-dataviz :data="dataViz">
      <svg slot="svg" class="scaling-svg" xmlns="http://www.w3.org/2000/svg" width="322" height="322" viewBox="0 0 322 322">
        <path fill="#f15929" d="M161 161L116.898 7.198C131.593 2.984 145.713 1 161 1v160z"/>
        <path fill="#900" d="M161 161L17.193 90.861c20.414-41.856 54.94-70.826 99.705-83.662L161 161z"/>
        <path fill="#FC0" d="M161 161V1c88.366 0 160 71.634 160 160s-71.634 160-160 160S1 249.366 1 161c0-26.421 4.611-46.392 16.193-70.139L161 161z"/>
        <linearGradient id="a" gradientUnits="userSpaceOnUse" x1="161" y1="301" x2="161" y2="21">
          <stop offset=".5" stop-color="#f7f7f5"/>
          <stop offset="1" stop-color="#fff"/>
        </linearGradient>
        <circle fill="url(#a)" cx="161" cy="161" r="140"/>
      </svg>
      <h3 slot="title" class="chartTitle">Marshall Student  <br /> Breakdown by Major</h3>
      <div slot="label" class="usc-data__label-container">
        <div class="usc-data__label-number">
          4,250
        </div>
        <div class="usc-data__label-sub">
          Marshall Students
        </div>
      </div>
    </usc-dataviz>
    <div class="body mb gutter-lg"> <div class="cardinal-line mt" style="margin-bottom: 1em;"></div></div>
    <div class="body mb gutter-lg">
      <h2>Emphases for Business Administration Students</h2>
      <p>Our emphases are optional specializations for students in our Business Administration degree program that allow you to further customize your education to suit your interests and career goals.</p>
      <p><i style="">Marshall’s emphases give you an opportunity to gain broad or deep expertise in one of nine areas that are in high demand among employers. No matter what career path you pursue, the knowledge and skills you gain through your chosen emphasis will give you a thorough preparation in some of the most relevant and impactful areas for the future of global business. Adding an emphasis to your résumé will increase your competitiveness on the job market.</i></p>
      <p>To be eligible to declare an emphasis, you must have sophomore standing and have spent one semester in residence at USC. You must also complete the gateway course for the emphasis. You may only declare one of the nine emphases offered, but you can combine your emphasis with one of our renowned joint or dual-degree programs, interdisciplinary offerings, one or more minors, and any of our other signature Marshall experiences. You can declare an emphasis up until the last day of class of your final semester as long as you meet the other requirements.</p>
      <div class="gridbox mb">
        <div class="gridbox-itemsB">
          <div class="gridbox-items--img"><img src="../../../static/Icon/23-businessanalytics.png" alt="Business Analytics"/></div>
        </div>
        <div class="gridbox-itemsB">
          <div class="gridbox-items--img"><img src="../../../static/Icon/23-comms.png" alt="Communication"/></div>
        </div>
        <div class="gridbox-itemsB">
          <div class="gridbox-items--img"><img src="../../../static/Icon/23-entrepreneurshipinnov.png" alt="Entrepreneurship and Innovation"/></div>
        </div>
        <div class="gridbox-itemsB">
          <div class="gridbox-items--img"><img src="../../../static/Icon/23-finance.png" alt="Finance"/></div>
        </div>
        <div class="gridbox-itemsB">
          <div class="gridbox-items--img"><img src="../../../static/Icon/23-leadershipinn.png" alt="Leadership and Innovation"/></div>
        </div>
        <div class="gridbox-itemsB">
          <div class="gridbox-items--img"><img src="../../../static/Icon/23-marketing.png" alt="Marketing"/></div>
        </div>
        <div class="gridbox-itemsB">
          <div class="gridbox-items--img"><img src="../../../static/Icon/23-realestatefinance.png" alt="Real Estate Finance"/></div>
        </div>
        <div class="gridbox-itemsB">
          <div class="gridbox-items--img"><img src="../../../static/Icon/23-riskmanagement.png" alt="Risk Management"/></div>
        </div>
      </div>
      <h3><strong>BUSINESS ANALYTICS <br />Data-driven decision making</strong></h3>
      <p>Big data drives most industries today, as well as the global economy. Through the emphasis in Business Analytics, you have a choice of pursuing a broad perspective — gaining such knowledge and skills as exploratory data analysis, data visualization and computer programming that support multiple business fields — or depth of knowledge and skills, such as machine learning, forecasting and optimization techniques that focus on specific business fields (e.g., operations-related or market-related analytics).</p>
      <p>Whichever path you select within the emphasis, you will learn how to turn data into meaningful business insights as you support organizations in analyzing and delivering data-driven decision-making, implementation and evaluation. Data-savvy leaders are needed across a wide range of industries, including healthcare, entertainment, energy, retail, technology, interactive gaming, media, sports, hospitality, automotive, consumer goods, transportation, logistics, manufacturing and financial services.</p>


      <h3><strong>COMMUNICATION <br />Versatile, in-demand skills across industries</strong></h3>
      <p>No matter what field of business or what industry you work in, exemplary communication skills are among the most prized by employers. Students in this emphasis will learn how to effectively engage and persuade organizational stakeholders to achieve results as well as lead and motivate diverse teams, in-person, virtually and in disperse locations globally. You’ll build skills in developing communication strategies, navigating complex communications situations and handling crisis communications.</p>
      <p>This emphasis will benefit students pursuing careers across all industries and fields. Particularly, anyone pursuing roles in consulting, marketing, sales, consumer products, media, entrepreneurship, healthcare and human resources that rely heavily on communication strategy and persuasion will greatly benefit from the Communication emphasis.</p>

      <h3><strong>ENTREPRENEURSHIP AND INNOVATION <br />Capable problem-solving in a dynamic world</strong></h3>
      <p>Developing an entrepreneurial mindset and the ability to identify, evaluate and pursue new and innovative opportunities will serve you well, whether your career takes you into the startup realm or employment in an existing organization. The Entrepreneurship and Innovation emphasis will help build your skills in problem-solving, creativity, adaptability, flexibility and decision-making in dynamic business situations in which calculated risk may lead to major reward. The emphasis signals to potential employers that you are a capable problem-solver who understands how to build, launch and grow new initiatives.</p>
      <p>Students in this emphasis will interact with founders and leaders from a wide range of organizations, who will share their experiences from idea through launch and growth. Experiential learning is a significant component of this emphasis, so you will be able to apply your learnings to diverse business opportunities.</p>

      <h3><strong>FINANCE <br />Analysis skills for decision making and value creation</strong></h3>
      <p>Choose classes that provide a breadth of knowledge and skills as a finance generalist — or those that provide deep knowledge and skills aligned with a specific subfield or career path. The latter include financial modeling and valuation, institutional and market knowledge, financial strategy — including risk management and capital raising — and investment and wealth-management strategies and techniques.</p>
      <p>Whichever focus you choose, you will learn the tools and frameworks that guide financial decisions and strategies, as well as how financial markets function and the roles of various institutions. You’ll gain essential analytical and critical-thinking skills as well as preparation to work in various capacities within the financial services industry, including corporations, government and nonprofits. The range of positions across corporate finance, financial services, investment management, and financial and economic consulting is vast. The emphasis also provides a strong foundation for graduate study in business, economics and law.</p>

      <h3><strong>INTERNATIONAL RELATIONS <br />Engaging a world of opportunities</strong></h3>
      <p>Bringing business acumen to the perspective of diplomatic engagement, this emphasis combines the strengths of USC Marshall and the USC School of International Relations. The wide-ranging course offerings include multinational enterprises and world politics, conflict mediation and negotiation, gender and global issues, ethnicity and nationalism, as well as region-specific topics relating to China, Russia, Latin America, Africa, the Middle East and more.</p>
      <p>You’ll gain expertise at the intersection of commercial, civic and political exchanges as well as a thorough grounding in issues relating to international security and how to succeed in the complex and dynamic global economy.</p>

      <h3><strong>LEADERSHIP AND INNOVATION <br />Vision and strategy for competitive advantage</strong></h3>
      <p>Students who choose this emphasis will learn how to create an inspiring vision and strategy for innovation in their organization, effectively lead high-performing innovative teams, create a culture of agility and innovation, and effectively identify and solve critical problems to build sustainable competitive advantage and satisfy a diverse set of stakeholders.</p>
      <p>Focus on either obtaining a breadth of knowledge and skills through diverse management and organization classes — or deeper knowledge and skills in a specific subfield such as global strategy and global leadership. Whichever you choose, you will be well-prepared to lead in a wide range of industries and organizations — from small entrepreneurial firms to large corporations.</p>

      <h3><strong>MARKETING <br />Strategies to address diverse consumer needs</strong></h3>
      <p>Marketing is key to every organization’s success, and students in the Marketing emphasis will develop the knowledge and skills to meet diverse consumer needs through innovative products and services, compelling messages, competitive pricing and effective distribution. You will choose to focus on general insight into this essential business function or build specific expertise in a subfield, such as digital marketing or sales. Either way, you will gain creative and analytical skills to help organizations across multiple industries develop, implement and evaluate effective marketing strategies.</p>
      <p>The Marketing emphasis prepares you for careers in brand management, advertising, public relations, digital marketing, sales, marketing research and analytics, and many other fields.</p>

      <h3><strong>REAL ESTATE FINANCE <br />Dynamic growth in an ever-changing market</strong></h3>
      <p>Real estate finance is a dynamic and competitive industry, and plays a major role in the global economy. Marshall has longstanding strengths in the field, and students in the Real Estate Finance emphasis will benefit from a particularly strong network of alumni working in this arena.</p>
      <p>Gain knowledge and skills that will help you perform complex tasks involving real estate finance and development, economics and state-of-the-art computer modeling. You’ll become well-versed in valuation and analysis, raising capital for real estate investment projects, the mortgage markets, financial modeling and more.</p>

      <h3><strong>RISK MANAGEMENT <br />Mitigation to maximize opportunity</strong></h3>
      <p>All careers and industries involve risk management. Given the growing complexity and interconnectedness of the global economy, more and more employers are seeking talent with risk management skills. Few colleges and universities offer an emphasis or program in risk management, so your selection of this emphasis will help you stand out in the marketplace.You will gain critical skills in how to identify, assess, control, finance and report risk. In addition, students in the Risk Management emphasis will have the opportunity to be paired with a mentor from the risk management profession as well as the chance to attend numerous networking and recruiting events. These events typically attract 40 employers a year, including banks, consulting firms and insurance companies.</p>

    </div>
      <usc-videoEmbed :frameTitle="this.video.frameTitle" :caption="this.video.caption" :videoSource="this.video.src"></usc-videoEmbed>
      <div class="body mb gutter-lg"><div class="cardinal-line mt"></div></div>

    <div class="body mb gutter-lg">
      <h2>Joint Programs</h2>
      <p>Building on USC’s interdisciplinary strengths and status as a leading research university, our joint degree programs offer the best of two worlds to students who want to pursue a business degree in combination with another leading USC academic program in one of four in-demand fields.</p>
      <div class="gridbox mb">
        <div class="gridbox-itemsB">
          <div class="gridbox-items--img"><img src="../../../static/Icon/23-ai-business.png" alt="AI for Business"/></div>
        </div>
        <div class="gridbox-itemsB">
          <div class="gridbox-items--img"><img src="../../../static/Icon/23-business-cinematic.png" alt="Business of Cinematic Arts"/></div>
        </div>
        <div class="gridbox-itemsB">
          <div class="gridbox-items--img"><img src="../../../static/Icon/23-real-estate-finance.png" alt="Real Estate Finance + Development"/></div>
        </div>
      </div>
    </div>
    <usc-videoEmbed :frameTitle="this.video2.frameTitle" :caption="this.video2.caption" :videoSource="this.video2.src"></usc-videoEmbed>
    <div class="body mb gutter-lg">

      <h3><strong>ARTIFICIAL INTELLIGENCE FOR BUSINESS (BUAI) <br />
      Harnessing technology to build opportunity</strong></h3>
      <p>From blockchain to self-driving vehicles to personalized medicine, AI is changing how we live and transforming every industry. Offered in partnership with the top-ranked USC Viterbi School of Engineering, the small-cohort Artificial Intelligence for Business degree (BUAI) prepares students to excel in this exciting field at the intersection of business and computer science. This first-of-its-kind program blends core business concepts with big data knowledge to foster the skills essential for leadership in an ever-evolving technological landscape.</p>

      <h3><strong>CINEMATIC ARTS (BCA) <br />
      Real-world creative leadership</strong></h3>
      <p>Taking advantage of our location in the world’s entertainment capital, the John H. Mitchell Business of Cinematic Arts (BCA) program pairs Marshall exceptional business curriculum with the top-ranked School of Cinematic Arts for interdisciplinary coursework and coveted internship experiences. You’ll gain unparalleled, specialized expertise that will distinguish you in the marketplace for both business and entertainment career opportunities.</p>

      <h3><strong>REAL ESTATE FINANCE + DEVELOPMENT (REFD) <br />
      Building communities</strong></h3>
      <p>Combining the strengths of the renowned Wilbur H. Smith III Department of Real Estate Development at USC Price and Marshall’s longstanding real estate finance curriculum, this brand-new joint degree focuses on integrated courses, cohort building and industry engagement. You’ll benefit from both schools’ strong ties with local, national and international real estate development and investment firms.</p>


    </div>

  <!-- <div class="body mb gutter-lg">
    <img src="../../../static/Icon/marshall-chart.png" />
  </div> -->
  </div>
</template>

<script>
import hero from '@/components/Hero'
import intro from '@/components/Intro'
import table from '@/components/Table'
import Cta from '@/components/Cta'
import DataViz from '@/components/DataViz'
import videoEmbed from '@/components/videoEmbed'
import calloutLink from '@/components/CalloutLink'

export default {
  name: 'Overview',
  components: {
    'usc-hero': hero,
    'usc-intro': intro,
    'usc-table': table,
    'usc-cta': Cta,
    'usc-dataviz': DataViz,
    'usc-videoEmbed': videoEmbed,
    'usc-calloutLink': calloutLink
  },
  data () {
    return {
      page: {
        color: '#ffcc00'
      },
      calloutLink: {
        title: 'Your Future, Your Way',
        url: 'https://curriculum.marshall.usc.edu/',
        urlText: 'Your Future, Your Way'
      },
      hero: {
        title: 'Academics',
        loResSrc: '../../../static/HeroImages/Academics6.jpg',
        hiResSrc: '../../../static/HeroImages/Academics6.jpg'
      },
      video: {
        caption: 'Raman Randhawa - Vice Dean, Undergraduate Programs',
        src: 'https://player.vimeo.com/video/743203385',
        frameTitle: 'Video: Raman Randhawa - Vice Dean, Undergraduate Programs'
      },
      video2: {
        caption: 'AI: Seed for Change or Existential Threat? Vishal Gupta - Associate Professor of Data Science and Operations',
        src: 'https://player.vimeo.com/video/642794506',
        frameTitle: 'AI: Seed for Change or Existential Threat? Video: Vishal Gupta - Associate Professor of Data Science and Operations'
      },
      table: {
        title: 'Curriculum Structure',
        data: [
          {
            left: 'USC Required Courses',
            right: '40'
          },
          {
            left: 'Marshall Core',
            right: '48'
          },
          {
            left: 'Marshall Upper-Division Electives',
            right: '12'
          },
          {
            left: 'Free Electives/Minor',
            right: '32*'
          }
        ],
        disclaimer: '*Subject to course selection and examination credit policy.'
      },
      dataViz: [
        {
          color: '#ffcc00',
          labelBold: '3,750',
          labelSmall: 'Business Administration'
        },
        {
          color: '#990000',
          labelBold: '300',
          labelSmall: 'Accounting'
        },
        {
          color: '#f15929',
          labelBold: '200',
          labelSmall: 'World Bachelor in Business'
        }
      ]
    }
  },
  created () {
    this.$root.setPageHeader('Academics', 'Overview')
  }
}
</script>

<style scoped lang="scss">
  img {
    max-width: 100%;
  }
  .gridbox {
    display: grid;
    flex-wrap: wrap;
    grid-template-columns: 1fr 1fr 1fr 1fr;
  }
  .gridbox-items {
    margin-right: 1em;
    width: 220px;
  }
  .gridbox-itemsB {
    margin-right: 1em;
    width: 180px;
  }
  .gridbox-items--text {
    font-weight: bold;
    text-align: center;
  }
  .cardinal-line.mt {
    background: #000;
    height: 2px;
    margin-top: 60px;
  }
  h3.chartTitle {
    font-size: 1.7rem !important;
    color: #464646;
    font-weight: bold !important;
    // font-family: 'Open Sans', sans-serif !important;
    margin-bottom: 2rem !important;
    @media (max-width: 1200px) {
      font-size: 1.5rem;
    }
    @media (max-width: 1038px) {
      font-size: 1.7rem;
    }
  }
    @media screen and (max-width: 1200px) {
    .gridbox {
      grid-template-columns: 1fr 1fr 1fr;
    }
  }
  @media screen and (max-width: 700px) {
    .gridbox {
      grid-template-columns: 1fr 1fr;
    }
  }
  @media screen and (max-width: 450px) {
    .gridbox {
      grid-template-columns: 1fr;
    }
    .gridbox-items, .gridbox-itemsB {
      margin: 0 auto !important;
    }
  }
</style>



// WEBPACK FOOTER //
// src/pages/academics/Overview.vue